import { styled } from '@mui/material/styles';
import type {
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
} from '@mui/x-data-grid-pro';
import type { RowHeights } from '@/shared/constants';
import { Analytics } from '@/shared/lib';
import type { ColumnsConfig } from '@/shared/types';
import {
  getColumnHeaderHeight,
  DataGrid as SharedDataGrid,
  useDataGridListeners,
} from '@/shared/ui';
import type { Highlight } from '../../model/types';
import { useDataFetchersContext } from '../DataFetchersContext/DataFetchersContext';
import { getRowHeight } from './getRowHeight';
import { useData } from './useData';
import { useTableProps } from './useTableProps';

const DataGrid = styled(SharedDataGrid)(({ theme }) => ({
  '& .MuiDataGrid-main': {
    '--DataGrid-topContainerHeight': '47px',
  },

  '& .dimensionCell + .metricCell, & .dimensionHeader + .metricHeader': {
    boxShadow: 'inset 8px 0 8px -8px #00234033',
  },

  '& .dimensionCell': {
    padding: `0 ${theme.spacing(1.25)}`,
    flexShrink: 0,
  },

  '& .columnGroup': {
    display: 'none',
  },
}));

interface Props {
  columnsWidth: Record<string, number | undefined>;
  columnsConfig: ColumnsConfig;
  rowHeight: RowHeights;
  dimensions: string[];
  metric: string;
  highlight: Highlight;
  reorderDimensions: (info: {
    id: string;
    oldIndex: number;
    newIndex: number;
  }) => void;
  updateColumnsWidth: (columnName: string, newWidth?: number) => void;
  isReadonly?: boolean;
}

export function DataTable({
  columnsWidth,
  columnsConfig,
  rowHeight,
  dimensions,
  metric,
  highlight,
  reorderDimensions,
  updateColumnsWidth,
  isReadonly,
}: Props) {
  const { onGetRows, onGetTotal } = useDataFetchersContext();
  const {
    total,
    rows,
    columns: dataColumns,
    loading,
    getNextPage,
  } = useData({
    getTotal: onGetTotal,
    getRowsPage: onGetRows,
  });
  const { columnGroupingModel, columns } = useTableProps({
    total,
    rowHeight,
    columnsWidth,
    columnsConfig,
    dimensions,
    metric,
    dataColumns,
    isReadonly,
    highlight,
  });

  useDataGridListeners();

  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    const { column } = params;
    const oldIndex = params.oldIndex;
    const targetIndex = params.targetIndex;

    if (oldIndex === targetIndex) {
      return;
    }

    reorderDimensions({
      id: column.field,
      oldIndex: oldIndex,
      newIndex: targetIndex,
    });
    Analytics.sendFeatureUsage('data_table', 'reorder_column', {
      page: window.location.pathname,
    });
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const { width, field } = params.colDef;

    updateColumnsWidth(field, width);
  };

  return (
    <DataGrid
      disableColumnResize={isReadonly}
      columnHeaderHeight={getColumnHeaderHeight(rowHeight)}
      getRowHeight={getRowHeight({ rowHeight, highlight })}
      loading={loading}
      columnGroupingModel={columnGroupingModel}
      columns={columns}
      rows={rows}
      getRowClassName={(params) => {
        return params.row.isTotal ? 'total' : '';
      }}
      onRowsScrollEnd={getNextPage}
      onColumnOrderChange={handleColumnOrderChange}
      onColumnWidthChange={handleColumnWidthChange}
    />
  );
}
